/**
 * Observed progress for a setup step (enabler) — V108-6-4 / ADR-0085 §3 (design pass 2026-10-11). PURE.
 *
 * A step's `state` (workspace_enablers) says what the customer decided and what the server observed
 * (`done` is observed, never claimed). Progress is the detail the setup screens show while a step is
 * moving, derived ONLY from what collectors and connections already record — no new collector output:
 *
 *   not_started  no connection of the kind yet
 *   waiting      a connection exists (or the vendor console was opened from GAC Pro, migration 625)
 *                and nothing has been read yet — "Waiting for our first read"
 *   failed       the latest attempt recorded an error newer than any success (`last_error_message`),
 *                or the only connection is in a non-counted environment (staging), or a quota deferral
 *   not_counted  V108-8-13: a CI gate step whose runs arrive but count for nothing yet (enablers/ci-trust.ts)
 *   partial      AC-3: a declared multi-account provider has production accounts not read yet
 *                (migration 626) — "1 of 3 accounts read"; nothing is credited until all are
 *   done         the server observed it (`verified_at`)
 *
 * Failure causes (`classifyConnectorFailure`) map an existing error message / deferral / environment
 * onto a small closed catalog with a generic fallback. `failureCauseView` is the ONE place a cause's
 * words and its single action link are built (AC-5: every screen — Setup, the coverage view, Today —
 * calls it, so a cause has one link everywhere). Raw vendor messages are never the headline; they
 * stay behind a "Details" disclosure.
 */
export const FAILURE_CAUSES = ['trust', 'staging', 'stack', 'key_rejected', 'permission', 'not_enabled', 'rate_limited', 'unknown'];
/** Kinds whose setup finishes in the vendor's console (a role or identity created there). */
export const CONSOLE_CONSENT_KINDS = new Set(['aws', 'gcp']);
const ms = (x) => {
    if (!x)
        return Number.NaN;
    return Date.parse(x.includes('T') ? x : `${x.replace(' ', 'T')}Z`);
};
const after = (a, b) => {
    const ta = ms(a);
    const tb = ms(b);
    return Number.isFinite(ta) && (!Number.isFinite(tb) || ta > tb);
};
const latest = (...xs) => {
    let best = null;
    for (const x of xs)
        if (x && (!best || ms(x) > ms(best)))
            best = x;
    return best;
};
/** `arn:aws:iam::123456789012:role/name` → `123456789012`. */
export function awsAccountFromArn(arn) {
    const m = /^arn:aws[a-z-]*:iam::(\d{12}):role\//.exec(arn ?? '');
    return m ? m[1] : null;
}
/** `arn:aws:iam::123456789012:role/path/name` → `name`. */
export function awsRoleNameFromArn(arn) {
    const m = /:role\/(?:.*\/)?([\w+=,.@-]+)$/.exec(arn ?? '');
    return m ? m[1] : null;
}
/** 123456789012 → 1234-5678-9012 (the AWS console's own grouping). */
export const formatAwsAccount = (id) => (/^\d{12}$/.test(id) ? `${id.slice(0, 4)}-${id.slice(4, 8)}-${id.slice(8)}` : id);
/** Map what a connection already records onto the cause catalog. Unknown messages fall back to `unknown`. */
export function classifyConnectorFailure(c, opts = {}) {
    const counted = opts.counted ?? ['production'];
    const now = (opts.now ?? new Date()).getTime();
    const msg = c.last_error_message ?? '';
    if (msg) {
        if (/sts:AssumeRole|AssumeRole|external ?id|not authorized to assume|trust (policy|relationship)|workload identity|getAccessToken/i.test(msg))
            return 'trust';
        if (/cloudformation|ROLLBACK_|CREATE_FAILED|stack .*(fail|roll)/i.test(msg))
            return 'stack';
        if (/\b401\b|invalid[ _-]?(api[ _-]?)?key|x-api-key|authentication_error|unauthori[sz]ed|revoked|invalid_grant|token (has )?expired|expired token/i.test(msg))
            return 'key_rejected';
        if (/\b429\b|rate.?limit|throttl|too many requests/i.test(msg))
            return 'rate_limited';
        if (/not enabled|is not turned on|not subscribed|has not been used in project|is disabled/i.test(msg))
            return 'not_enabled';
        if (/\b403\b|access ?denied|forbidden|insufficient (scope|permission)|missing (scope|permission)|permission denied|not authorized/i.test(msg))
            return 'permission';
        return 'unknown';
    }
    if (c.discovery_deferred_until && ms(c.discovery_deferred_until) > now)
        return 'rate_limited';
    if (!counted.includes(c.environment))
        return 'staging';
    return null;
}
/**
 * Progress for one connect step (subject `kind:<kind>`), from the connections of that kind.
 * `verifiedAt` is the enabler row's verified_at (done is observed by the server, not here).
 */
export function connectProgress(input) {
    const counted = input.counted ?? ['production'];
    const of = input.connectors.filter((c) => c.kind === input.kind && c.status !== 'disabled');
    const base = {
        state: 'not_started', connector_id: null, kind: input.kind, environment: null, needs_console: CONSOLE_CONSENT_KINDS.has(input.kind),
        consent_started_at: null, last_attempt_at: null, first_read_at: null, target_label: null, cause: null, accounts: null, fix_context: null,
    };
    if (!of.length)
        return input.verifiedAt ? { ...base, state: 'done', first_read_at: input.verifiedAt } : base;
    // The most relevant connection: a counted one first, then the newest activity.
    const rank = (c) => (counted.includes(c.environment) ? 1 : 0);
    const sorted = [...of].sort((a, b) => rank(b) - rank(a) || ms(latest(b.last_success_at, b.last_error_at, b.consent_started_at, b.configured_at)) - ms(latest(a.last_success_at, a.last_error_at, a.consent_started_at, a.configured_at)));
    const c = sorted[0];
    const label = targetLabel(c);
    const common = {
        ...base, connector_id: c.connector_id, environment: c.environment, consent_started_at: c.consent_started_at,
        last_attempt_at: latest(c.last_run_at, c.last_error_at), target_label: label,
        fix_context: { role_arn: c.kind === 'aws' ? c.target : null, account_id: c.account_id, external_id: c.external_id, host_project_id: c.host_project_id },
    };
    // AC-3: declared production accounts not read yet → partial, whatever else is true.
    const declared = (input.declaredAccounts ?? []);
    if (declared.length && input.kind === 'aws') {
        const readIds = new Set(of.filter((x) => counted.includes(x.environment) && x.status === 'active' && x.last_success_at && !after(x.last_error_at, x.last_success_at)).map((x) => x.account_id).filter((x) => !!x));
        const prod = declared.filter((a) => a.state === 'production');
        const accounts = {
            read: prod.filter((a) => readIds.has(a.account_id)),
            unread: prod.filter((a) => !readIds.has(a.account_id)),
            not_production: declared.filter((a) => a.state === 'not_production'),
        };
        if (accounts.read.length && accounts.unread.length)
            return { ...common, state: 'partial', accounts, first_read_at: c.last_success_at };
        if (!accounts.unread.length && input.verifiedAt)
            return { ...common, state: 'done', accounts, first_read_at: input.verifiedAt };
        common.accounts = accounts;
    }
    if (input.verifiedAt)
        return { ...common, state: 'done', first_read_at: input.verifiedAt };
    const failedNow = c.last_error_message && after(c.last_error_at, c.last_success_at);
    const code = failedNow
        ? classifyConnectorFailure(c, { counted, now: input.now })
        : !counted.includes(c.environment) && c.last_success_at
            ? 'staging'
            : c.discovery_deferred_until && ms(c.discovery_deferred_until) > (input.now ?? new Date()).getTime() ? 'rate_limited' : null;
    if (code)
        return { ...common, state: 'failed', cause: { code, detail: failedNow ? truncate(c.last_error_message) : null } };
    return { ...common, state: 'waiting' };
}
function truncate(s) {
    const t = s.replace(/\s+/g, ' ').trim();
    return t.length > 240 ? `${t.slice(0, 239)}…` : t;
}
function targetLabel(c) {
    if (c.kind === 'aws' && c.account_id)
        return `account ${formatAwsAccount(c.account_id)}`;
    if (c.kind === 'gcp' && c.host_project_id)
        return `project ${c.host_project_id}`;
    const t = (c.target ?? '').trim();
    if (!t)
        return null;
    try {
        return /^https?:\/\//.test(t) ? new URL(t).host : t;
    }
    catch {
        return t;
    }
}
const VENDOR = { aws: 'AWS', gcp: 'Google Cloud', okta_posture: 'Okta', okta: 'Okta', entra: 'Microsoft Entra', entra_posture: 'Microsoft Entra', github: 'GitHub', anthropic: 'Anthropic', openai: 'OpenAI', google_workspace: 'Google Workspace', microsoft_365: 'Microsoft 365' };
export const vendorName = (kind) => VENDOR[kind] ?? kind.replace(/_/g, ' ').replace(/\b\w/g, (x) => x.toUpperCase());
/**
 * Words + the single action for a cause. `slug` builds in-app links; the connection supplies the
 * role / project for console links. Pure; the SPA calls this on Setup, the coverage view and Today.
 */
export function failureCauseView(code, ctx) {
    const app = `/w/${encodeURIComponent(ctx.slug)}/app`;
    const vendor = vendorName(ctx.kind);
    const fixConnection = { label: `Fix the ${vendor} connection`, href: ctx.connector_id ? `${app}/setup#posture-${ctx.connector_id}` : `${app}/setup?connect=${encodeURIComponent(ctx.kind)}`, external: false };
    switch (code) {
        case 'trust': {
            if (ctx.kind === 'aws') {
                const role = awsRoleNameFromArn(ctx.role_arn);
                return {
                    code, line: "Role trust failed: the external ID doesn't match.",
                    fix: "Paste this external ID into the role's trust policy, then check again.",
                    action: role
                        ? { label: 'Open the role in AWS', href: `https://console.aws.amazon.com/iam/home#/roles/details/${encodeURIComponent(role)}?section=trust_relationships`, external: true }
                        : fixConnection,
                    paste: ctx.external_id ? { label: 'External ID', value: ctx.external_id } : null,
                };
            }
            if (ctx.kind === 'gcp') {
                return {
                    code, line: "Google Cloud didn't let GAC Pro use the service account.",
                    fix: 'Check the workload identity pool and the service account binding, then check again.',
                    action: { label: 'Open workload identity in Google Cloud', href: `https://console.cloud.google.com/iam-admin/workload-identity-pools${ctx.host_project_id ? `?project=${encodeURIComponent(ctx.host_project_id)}` : ''}`, external: true },
                    paste: null,
                };
            }
            return { code, line: `${vendor} didn't accept GAC Pro's identity.`, fix: 'Reconnect with the read-only access the form asks for.', action: fixConnection, paste: null };
        }
        case 'staging':
            return {
                code, line: `We reached ${ctx.account_id ? `account ${formatAwsAccount(ctx.account_id)}` : ctx.target ?? `the ${vendor} connection`}, which is labelled staging.`,
                fix: 'Sign in to the production account first. Staging reads show as a preview only.',
                action: { label: `Connect ${vendor} production`, href: `${app}/setup?connect=${encodeURIComponent(ctx.kind)}`, external: false },
                paste: null,
            };
        case 'stack':
            return {
                code, line: "The CloudFormation stack didn't finish.",
                fix: 'The stack events show which resource failed. Fix it, then create the stack again.',
                action: { label: 'See CloudFormation events', href: 'https://console.aws.amazon.com/cloudformation/home#/stacks', external: true },
                paste: null,
            };
        case 'key_rejected':
            return { code, line: `${vendor} rejected the key. It may have been revoked.`, fix: 'Create a new key and paste it in.', action: { label: 'Paste a new key', href: fixConnection.href, external: false }, paste: null };
        case 'permission':
            return { code, line: `${vendor} refused a read. The access is missing a permission.`, fix: 'Grant the read-only access the connection lists, then check again.', action: fixConnection, paste: null };
        case 'not_enabled':
            return { code, line: `A ${vendor} service GAC Pro reads is turned off.`, fix: 'Turn the service on, or tell us it is not used, then check again.', action: fixConnection, paste: null };
        case 'rate_limited':
            return { code, line: `${vendor} asked us to slow down.`, fix: 'Nothing to do. We try again automatically.', action: { label: `See the ${vendor} connection`, href: fixConnection.href, external: false }, paste: null };
        case 'unknown':
        default:
            return { code: 'unknown', line: `We couldn't read from ${vendor}.`, fix: 'Open the connection to see what went wrong.', action: fixConnection, paste: null };
    }
}
//# sourceMappingURL=progress.js.map